/**
 * WorkspaceRouter
 * Extracts :wsSlug from URL params and syncs it with WorkspaceContext.
 * Redirects to the default workspace if the slug is not found.
 * Always renders <Outlet /> — never blocks with a spinner.
 */

import { useLayoutEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { Sentry } from '@martha-v5/sentry/browser';
import { useWorkspace } from '../../contexts/WorkspaceContext';

export function WorkspaceRouter() {
  const { wsSlug } = useParams<{ wsSlug: string }>();
  const navigate = useNavigate();
  const {
    workspaces,
    currentWorkspace,
    isLoadingWorkspaces,
    setCurrentWorkspaceBySlug,
  } = useWorkspace();

  useLayoutEffect(() => {
    if (isLoadingWorkspaces || !wsSlug) return;

    // Already on the right workspace
    if (currentWorkspace?.slug === wsSlug) return;

    const found = setCurrentWorkspaceBySlug(wsSlug);
    if (!found && workspaces.length > 0) {
      // Slug not found — redirect to default workspace
      const defaultSlug = workspaces[0].slug || 'archie';
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Workspace "${wsSlug}" not found — redirecting to "${defaultSlug}"`,
        level: 'warning',
      });
      navigate(`/w/${defaultSlug}`, { replace: true });
    }
  }, [wsSlug, currentWorkspace?.slug, workspaces, isLoadingWorkspaces, setCurrentWorkspaceBySlug, navigate]);

  // Always render Outlet — sidebar and layout stay visible while workspace loads
  return <Outlet />;
}
